<template>
    <div class="space-y-1 w-full">
      <!-- Column on xs, row on sm+, equal flex on sm, wider code box on md+ -->
      <div class="flex flex-row w-full gap-1">
        <!-- Country dial‑code selector -->
        <Multiselect
          v-model="selectedCode"
          :options="filteredCountries"
          track-by="code"
          label="label"
          value-prop="code"
          class="phone-select border-0 w-1/2 sm:w-1/3 md:w-1/2 xl:w-2/5"
          :searchable="!disabled"   
          :filter-results="false"
          :disabled="disabled"
          @search-change="handleSearch"
          @select="onCountrySelect"
          :close-on-select="true"
          :clear-on-select="false"
          :allow-empty="false"
          :canClear="false"
        />
  
        <!-- Local phone number (national part) -->
        <input
          v-model.trim="localNumber"
          @input="update"
          type="tel"
          name="tel-national"
          autocomplete="tel-national"
          class="border border-gray-400 p-2 rounded-md md:rounded-md mt-0 w-1/2 sm:w-2/3 md:w-1/2 xl:w-3/5"
          :class="{ 'border-red': !!computedError }"
          :disabled="disabled"
          placeholder="712345678"
        />
  
        <!-- Hidden country code for autofill -->
        <input
          type="hidden"
          :value="selectedDial != null ? selectedDial.replace('+', '') : null"
          name="tel-country-code"
          autocomplete="tel-country-code"
        />
      </div>
  
      <!-- Validation error -->
      <span v-if="!disabled && computedError" class="alert">{{ computedError }}</span>
    </div>
  </template>
  
  <script>
  import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from 'libphonenumber-js';
  import Multiselect from '@vueform/multiselect';
  import '@vueform/multiselect/themes/default.css';
  
  export default {
    name: 'PhoneInput',
    components: { Multiselect },
    props: {
      modelValue: String,
      disabled: Boolean,
      error: String,
    },
    emits: ['update:modelValue', 'update:error'],
    data() {
      return {
        countries: [],
        searchTerm: '',
        selectedCode: 'RO',
        selectedDial: '+40',
        localNumber: '',
        internalError: null,
      };
    },
    computed: {
      computedError() {
        return this.error ?? this.internalError;
      },
      filteredCountries() {
        if (!this.searchTerm) return this.countries;
        const term = this.searchTerm.toLowerCase().replace('+', '').trim();
        return this.countries.filter((o) =>
          o.code.toLowerCase().includes(term) ||
          o.dial.replace('+', '').includes(term) ||
          o.label.toLowerCase().includes(term)
        );
      },
    },
    watch: {
      modelValue: {
        immediate: true,
        handler(v) {
          if (!v) return;
          let normalized = v.trim();
          if (normalized.startsWith('00')) normalized = '+' + normalized.slice(2);
          if (!normalized.startsWith('+')) normalized = '+' + normalized;
          const phone = parsePhoneNumberFromString(normalized);
          if (phone) {
            this.selectedCode = phone.country;
            this.selectedDial = `+${phone.countryCallingCode}`;
            this.localNumber = phone.nationalNumber || '';
          }
        },
      },
      error: {
        immediate: true,
        handler(val) {
          this.internalError = val;
        },
      },
    },
    created() {
      this.countries = getCountries()
        .map((c) => ({
          code: c,
          dial: `+${getCountryCallingCode(c)}`,
          label: `${c} +${getCountryCallingCode(c)}`,
        }))
        .sort((a, b) => a.code.localeCompare(b.code));
    },
    methods: {
      onCountrySelect(value, option) {
        console.log(option);
        // Full country object received here
        if (option) {
          // ensure selectedDial uses the dial from option (unique even if pref duplicates)
          this.selectedCode = option.code;
          this.selectedDial = option.dial;
          // propagate full object upward if parent cares
          this.$emit('country-select', option);
          // re‑run validation after changing selection
          this.update();
        }
      },
      handleSearch(q) {
        this.searchTerm = q;
      },
      update() {
        if (this.disabled) return;
        const digits = this.localNumber.replace(/\D/g, '');
        this.localNumber = digits;
        const full = `${this.selectedDial}${digits}`;
        const phone = parsePhoneNumberFromString(full);
        this.internalError = phone && phone.isValid() ? null : this.$t('phone_invalid');
        this.$emit('update:error', this.internalError);
        this.$emit('update:modelValue', full);
      },
    },
  };
  </script>
  
  <style scoped>
  @import '@vueform/multiselect/themes/default.css';
  
  /* remove right border when in row layout (md+) */
  @media (min-width: 768px) {
    .phone-select:deep(.multiselect) {
      border-right: none !important;
    }
  }
  
  .phone-select:deep(.multiselect-option.is-selected) {
    background-color: #5f2eea !important;
    color: #fff !important;
  }
  
  .border-red {
    border-color: #e20000 !important;
  }
  
  .alert {
    height: 48px;
    background-color: #ffc8c8;
    color: #e20000;
    display: flex;
    align-items: center;
    position: relative;
    padding-left: 40px;
    border-radius: 10px;
    margin-top: 10px;
  }
  
  .alert::before {
    content: '!';
    position: absolute;
    display: flex;
    font-size: 14px;
    line-height: 20px;
    font-weight: bold;
    left: 10px;
    top: 13px;
    background-color: #ff0000;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #ffc8c8;
    align-items: center;
    justify-content: center;
  }
  </style>
  